import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  SelectChangeEvent,
  CircularProgress,
  InputAdornment,
  IconButton,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import DevicesIcon from '@mui/icons-material/Devices';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useSnackbar } from 'notistack';
import { format, parseISO } from 'date-fns';
import { ru, enUS } from 'date-fns/locale';
import api from '../api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

interface Device {
  id: number;
  inventoryNumber: string;
  type: string;
  model: string;
  serialNumber: string | null;
  status: string;
  purchaseDate: string | null;
  supplier: string | null;
  cost: number | null;
  condition: string | null;
  organization: { id: number; name: string } | null;
  currentAssignee: { id: number; fullName: string; organization: { id: number; name: string } } | null;
}

interface Organization {
  id: number;
  name: string;
  address: string | null;
  contacts: string | null;
}

interface PaginatedResponse {
  data: Device[];
  total: number;
  page: number;
  totalPages: number;
}

interface DeviceType {
  id: number;
  name: string;
  deviceCount: number;
}

const DEVICE_CONDITION_KEYS = ['new', 'used', 'needsRepair'] as const;

const getStatusLabels = (t: ReturnType<typeof useTranslation>['t']): Record<string, string> => ({
  in_stock: t('devices.statusInStock'),
  issued: t('devices.statusIssued'),
  written_off: t('devices.statusWrittenOff'),
});

const STATUS_COLORS: Record<string, 'success' | 'primary' | 'error'> = {
  in_stock: 'success',
  issued: 'primary',
  written_off: 'error',
};

interface DeviceForm {
  inventoryNumber: string;
  type: string;
  model: string;
  serialNumber: string;
  purchaseDate: string;
  supplier: string;
  cost: string;
  condition: string;
  organizationId: number | '';
}

const emptyForm: DeviceForm = {
  inventoryNumber: '',
  type: '',
  model: '',
  serialNumber: '',
  purchaseDate: '',
  supplier: '',
  cost: '',
  condition: '',
  organizationId: '',
};

function deviceToForm(d: Device): DeviceForm {
  return {
    inventoryNumber: d.inventoryNumber,
    type: d.type,
    model: d.model,
    serialNumber: d.serialNumber || '',
    purchaseDate: d.purchaseDate ? d.purchaseDate.substring(0, 10) : '',
    supplier: d.supplier || '',
    cost: d.cost != null ? String(d.cost) : '',
    condition: d.condition || '',
    organizationId: d.organization?.id || '',
  };
}

export default function DevicesPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const locale = useMemo(() => language === 'ru' ? ru : enUS, [language]);
  const statusLabels = getStatusLabels(t);

  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [urlParams] = useSearchParams();
  const initialStatus = urlParams.get('status') || 'all';
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDeviceId, setEditingDeviceId] = useState<number | null>(null);
  const [form, setForm] = useState<DeviceForm>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof DeviceForm, string>>>({});
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);

  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffDevice, setWriteOffDevice] = useState<Device | null>(null);
  const [writeOffReason, setWriteOffReason] = useState('');

  const [issueOpen, setIssueOpen] = useState(false);
  const [issueDevice, setIssueDevice] = useState<Device | null>(null);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page + 1));
      params.set('limit', String(rowsPerPage));
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('type', typeFilter);

      const { data } = await api.get<PaginatedResponse>(`/devices?${params}`);
      setDevices(data.data);
      setTotal(data.total);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('devices.errorLoading'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, typeFilter, enqueueSnackbar, t]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    api.get<Organization[]>('/organizations').then(({ data }) => setOrganizations(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get<DeviceType[]>('/device-types')
      .then(({ data }) => setDeviceTypes(data.map((t) => t.name)))
      .catch(() => {});
  }, []);

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof DeviceForm, string>> = {};
    if (!form.inventoryNumber.trim()) errors.inventoryNumber = t('common.required');
    if (!form.type) errors.type = t('common.required');
    if (!form.model.trim()) errors.model = t('common.required');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);

    const payload = {
      inventoryNumber: form.inventoryNumber || undefined,
      type: form.type,
      model: form.model,
      serialNumber: form.serialNumber || null,
      purchaseDate: form.purchaseDate || null,
      supplier: form.supplier || null,
      cost: form.cost ? parseFloat(form.cost) : null,
      condition: form.condition || null,
      organizationId: form.organizationId || null,
    };

    try {
      if (editingDeviceId != null) {
        await api.put(`/devices/${editingDeviceId}`, payload);
        enqueueSnackbar(t('devices.snackbarUpdated'), { variant: 'success' });
      } else {
        await api.post('/devices', payload);
        enqueueSnackbar(t('devices.snackbarAdded'), { variant: 'success' });
      }
      setDialogOpen(false);
      setEditingDeviceId(null);
      setForm(emptyForm);
      fetchDevices();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateNumber = useCallback(async () => {
    try {
      const { data } = await api.get<{ inventoryNumber: string }>('/devices/next-number');
      setForm((f) => ({ ...f, inventoryNumber: data.inventoryNumber }));
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || t('common.error'), { variant: 'error' });
    }
  }, [enqueueSnackbar, t]);

  const handleWriteOff = async () => {
    if (!writeOffDevice || !writeOffReason.trim()) return;
    try {
      await api.post(`/devices/${writeOffDevice.id}/write-off`, {
        reason: writeOffReason,
        date: new Date().toISOString(),
      });
      enqueueSnackbar(t('devices.snackbarWrittenOff'), { variant: 'success' });
      setWriteOffOpen(false);
      setWriteOffDevice(null);
      setWriteOffReason('');
      fetchDevices();
    } catch (err: any) {
      const backendError = err.response?.data?.error;
      let errorMessage: string;
      if (backendError === 'Device is already written off') {
        errorMessage = t('writeOffs.writeOffErrorAlreadyWrittenOff');
      } else if (backendError === 'Cannot write off an issued device. Return it first') {
        errorMessage = t('writeOffs.writeOffErrorIssued');
      } else if (backendError === 'Device not found') {
        errorMessage = t('writeOffs.writeOffErrorNotFound');
      } else {
        errorMessage = backendError || t('common.error');
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const activeFilters = [
    statusFilter !== 'all',
    typeFilter !== 'all',
    search.trim() !== '',
  ].filter(Boolean).length;

  return (
    <Box>
      {/* Intro */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'var(--text-muted)' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              background: isDark
                ? 'linear-gradient(135deg, #8b8bff 20%, #5ee7f1 80%)'
                : 'linear-gradient(135deg, #5b5bf6 20%, #22c3d1 80%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: isDark
                ? '0 8px 20px -8px rgba(139,139,255,0.7)'
                : '0 8px 20px -8px rgba(91,91,246,0.5)',
            }}
          >
            <DevicesIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="body2">
            {t('devices.inSystem')}: <strong style={{ color: 'var(--text)' }}>{total}</strong> {t('common.records')}
            {activeFilters > 0 && (
              <> · {t('devices.activeFilters')}: <strong style={{ color: 'var(--primary)' }}>{activeFilters}</strong></>
            )}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditingDeviceId(null); setForm(emptyForm); setFormErrors({}); setDialogOpen(true); handleGenerateNumber(); }}>
          {t('devices.addDevice')}
        </Button>
      </Box>

{/* Filter bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 1.5,
          border: '1px solid var(--border)',
          background: 'var(--surface)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            sx={{ flex: '1 1 300px' }}
            placeholder={t('devices.searchPlaceholder')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'var(--text-muted)' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('common.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('common.status')}
              onChange={(e: SelectChangeEvent) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="in_stock">{t('devices.statusInStock')}</MenuItem>
              <MenuItem value="issued">{t('devices.statusIssued')}</MenuItem>
              <MenuItem value="written_off">{t('devices.statusWrittenOff')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>{t('common.type')}</InputLabel>
            <Select
              value={typeFilter}
              label={t('common.type')}
              onChange={(e: SelectChangeEvent) => { setTypeFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              {deviceTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {activeFilters > 0 && (
            <Button
              size="small"
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setTypeFilter('all');
                setPage(0);
              }}
            >
              {t('devices.clearFilters')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('devices.tableInvNumber')}</TableCell>
                <TableCell>{t('devices.tableTypeModel')}</TableCell>
                <TableCell>{t('devices.tableStatus')}</TableCell>
                <TableCell>{t('devices.tableAssignee')}</TableCell>
                <TableCell>{t('devices.tablePurchaseDate')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell>
                </TableRow>
              ) : devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'var(--text-muted)' }}>{t('devices.noDevices')}</TableCell>
                </TableRow>
              ) : (
                devices.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>
                        {d.inventoryNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {d.model}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block' }}>
                        {d.type}
                      </Typography>
                      {d.organization && (
                        <Typography variant="caption" sx={{ color: 'var(--text-muted)', display: 'block', fontSize: 11 }}>
                          {d.organization.name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[d.status] || d.status}
                        color={STATUS_COLORS[d.status]}
                        size="small"
                        sx={{
                          background:
                            d.status === 'in_stock'
                              ? isDark ? 'rgba(52,211,153,0.15)' : 'rgba(16,185,129,0.12)'
                              : d.status === 'issued'
                              ? isDark ? 'rgba(139,139,255,0.15)' : 'rgba(91,91,246,0.12)'
                              : isDark ? 'rgba(248,113,113,0.15)' : 'rgba(239,68,68,0.12)',
                          color: 'inherit',
                          border: 'none',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: 'var(--text-muted)' }}>
                      {d.currentAssignee ? (
                        <>
                          <Typography variant="body2" sx={{ color: 'var(--text)', fontWeight: 500 }}>
                            {d.currentAssignee.fullName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                            {d.currentAssignee.organization.name}
                          </Typography>
                        </>
                      ) : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-muted)' }}>
                      {d.purchaseDate ? format(parseISO(d.purchaseDate), 'dd.MM.yyyy', { locale }) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" gap={0.75} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setEditingDeviceId(d.id);
                            setForm(deviceToForm(d));
                            setFormErrors({});
                            setDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {t('devices.editButton')}
                        </Button>
                        {d.status === 'in_stock' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setIssueDevice(d); setIssueOpen(true); }}
                          >
                            {t('devices.issueButton')}
                          </Button>
                        )}
                        {d.status === 'in_stock' && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => { setWriteOffDevice(d); setWriteOffOpen(true); }}
                          >
                            {t('devices.writeOffButton')}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage={t('common.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common.fromToOf', { from, to, count })}
        />
      </Paper>

      {/* Device add/edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingDeviceId(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingDeviceId != null ? t('devices.editDevice') : t('devices.newDevice')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formInventoryNumber')} required
                value={form.inventoryNumber} onChange={(e) => setForm({ ...form, inventoryNumber: e.target.value })}
                error={!!formErrors.inventoryNumber} helperText={formErrors.inventoryNumber}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleGenerateNumber} title={t('devices.generateNumber')} disabled={submitting}>
                        <AutorenewIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth error={!!formErrors.type}>
                <InputLabel>{t('devices.formType')} *</InputLabel>
                <Select value={form.type} label={t('devices.formType') + ' *'} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {deviceTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('devices.formOrganization')}</InputLabel>
                <Select value={form.organizationId} label={t('devices.formOrganization')} onChange={(e) => setForm({ ...form, organizationId: e.target.value as number | '' })}>
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formModel')} required
                value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
                error={!!formErrors.model} helperText={formErrors.model}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formSerialNumber')}
                value={form.serialNumber} onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formPurchaseDate')} type="date"
                InputLabelProps={{ shrink: true }}
                value={form.purchaseDate} onChange={(e) => setForm({ ...form, purchaseDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formSupplier')}
                value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth label={t('devices.formCost')} type="number"
                value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{t('devices.formCondition')}</InputLabel>
                <Select value={form.condition} label={t('devices.formCondition')} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                  {DEVICE_CONDITION_KEYS.map((key) => <MenuItem key={key} value={t(`devices.conditions.${key}`)}>{t(`devices.conditions.${key}`)}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingDeviceId(null); }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Write-off dialog */}
      <Dialog open={writeOffOpen} onClose={() => setWriteOffOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('devices.writeOffDevice')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'var(--text-muted)' }}>
            {writeOffDevice?.inventoryNumber} — {writeOffDevice?.model}
          </Typography>
          <TextField
            fullWidth label={t('devices.formWriteOffReason')} required multiline rows={3}
            value={writeOffReason} onChange={(e) => setWriteOffReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteOffOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleWriteOff}
            disabled={!writeOffReason.trim()}
          >
            {t('devices.writeOffButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Issue redirect dialog */}
      <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('devices.issueDevice')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('devices.deviceReady', { device: `${issueDevice?.inventoryNumber} — ${issueDevice?.model}` })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('devices.goToIssueSection')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            onClick={() => { setIssueOpen(false); navigate('/assignments'); }}
          >
            {t('devices.goToIssue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}